import React, { useState, useEffect } from 'react';
import { FiX, FiSearch, FiUser } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean']
  ]
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'link'
];

const ProjectModal = ({ project, onClose, onSave, clients, users }) => {
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    status: 'active',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    if (project) {
      const selectedClient = clients.find(c => c.id === project.client_id);
      setClientSearch(selectedClient ? selectedClient.name : '');
      setFormData({
        name: project.name || '',
        description: project.description || '',
        client_id: project.client_id ? String(project.client_id) : '',
        status: project.status || 'active',
        start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
        end_date: project.end_date ? new Date(project.end_date).toISOString().split('T')[0] : ''
      });
      // Load existing team members if editing
      if (project.ProjectMemberAssignments) {
        const members = project.ProjectMemberAssignments.map(a => a.User || users.find(u => u.id === a.user_id)).filter(Boolean);
        setSelectedTeam(members);
      } else {
        setSelectedTeam([]);
      }
    } else {
      setSelectedTeam([]);
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'client_id' && value === '') {
      setFormData(prev => ({ ...prev, [name]: null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };



  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, team_members: selectedTeam.map(u => u.id) });
  };

  const filteredTeamUsers = (users || []).filter(u =>
    u.name.toLowerCase().includes(teamSearch.toLowerCase()) &&
    !selectedTeam.find(t => t.id === u.id)
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {project ? 'Edit Project' : 'Create Project'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Project Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="mt-1">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    style={{ height: '200px', marginBottom: '42px' }}
                    placeholder="Enter project description..."
                  />
                </div>
              </div>

              <div className="mb-6 relative">
                <label className="block text-sm font-medium text-gray-700">Client *</label>
                <input
                  type="text"
                  placeholder="Search client..."
                  value={clientSearch}
                  onChange={e => { setClientSearch(e.target.value); setShowClientDropdown(true); setFormData(prev => ({ ...prev, client_id: '' })); }}
                  onFocus={() => setShowClientDropdown(true)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                  required={!formData.client_id}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {showClientDropdown && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                    {[...clients]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase()))
                      .map(client => (
                        <li
                          key={client.id}
                          onMouseDown={() => { setFormData(prev => ({ ...prev, client_id: String(client.id) })); setClientSearch(client.name); setShowClientDropdown(false); }}
                          className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50"
                        >
                          {client.name}
                        </li>
                      ))}
                    {clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())).length === 0 && (
                      <li className="px-3 py-2 text-sm text-gray-400">No clients found</li>
                    )}
                  </ul>
                )}
                <input type="hidden" name="client_id" value={formData.client_id} />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="active">Active</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                    End Date
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>



              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Team Members</label>
                {selectedTeam.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedTeam.map(user => (
                      <div key={user.id} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center">
                        <FiUser className="mr-1" size={12} />
                        {user.name}
                        <button type="button" onClick={() => setSelectedTeam(prev => prev.filter(u => u.id !== user.id))} className="ml-2 text-blue-600 hover:text-blue-800">
                          <FiX size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <FiSearch className="absolute left-3 top-2.5 text-gray-400" size={14} />
                  <input
                    type="text"
                    placeholder="Search team members..."
                    value={teamSearch}
                    onChange={e => setTeamSearch(e.target.value)}
                    className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                {teamSearch && (
                  <div className="border border-gray-200 rounded-md max-h-32 overflow-y-auto mt-1">
                    {filteredTeamUsers.length > 0 ? filteredTeamUsers.map(user => (
                      <button key={user.id} type="button"
                        onClick={() => { setSelectedTeam(prev => [...prev, user]); setTeamSearch(''); }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center text-sm border-b border-gray-100 last:border-b-0"
                      >
                        <FiUser className="mr-2" size={14} />
                        <span className="font-medium">{user.name}</span>
                        <span className="ml-2 text-gray-500">({user.role})</span>
                      </button>
                    )) : <div className="px-3 py-2 text-sm text-gray-500">No users found</div>}
                  </div>
                )}
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="submit"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm"
                >
                  {project ? 'Update Project' : 'Create Project'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;